import {
  CalculatorIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  PrinterIcon,
  TruckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import FormFieldGroup from '../../components/ui/Form/FormFieldGroup';
import AdaptiveGrid from '../../components/ui/Grid/AdaptiveGrid';
import FlexibleContainer from '../../components/ui/Layout/FlexibleContainer';
import CollapsibleSection from '../../components/ui/Section/CollapsibleSection';
import StableDropdown from '../../components/ui/StableDropdown';
import { useDebounce } from '../../hooks/useDebounce';
import { apiService } from '../../services/api';
import { downloadDealDocuments } from '../../services/dealDocuments';
import { buildCustomerSearchEndpoint, buildVehicleSearchEndpoint } from './dealCalculatorSearch';

// BHPH is short paper: dealers write 6-24 month contracts (and often re-assign them to a
// larger institution), so the old 24-72 list had no usable BHPH option at all. These are
// QUICK PICKS, not a whitelist -- the term field takes any number of months (Jose 2026-07-12).
const TERM_QUICK_PICKS: Record<'cash' | 'financing' | 'bhph', number[]> = {
  bhph: [6, 12, 18, 24],
  financing: [24, 36, 48, 60, 72],
  cash: [],
};

const DEFAULT_TERM_MONTHS: Record<'cash' | 'financing' | 'bhph', number> = {
  bhph: 24,
  financing: 60,
  cash: 0,
};

// Local interfaces
interface StableDropdownOption {
  value: string;
  text: string;
  subtitle?: string;
  data?: unknown;
}

// API response interfaces
// Serialized services.deal_calculator.DealFinancials (WS3 Task 8) -- the ONE
// preview endpoint's response shape (POST /deals/calculate). Replaces the old
// credit-tier/DTI/deal-score "enhanced" shape the former E3 duplicate engine
// (calculations.calculate_enhanced_deal_structure, deleted) used to return.
// The canonical engine is loan math only (tax/fees/amortization) -- it does
// not compute credit-tier suggestions, DTI approval, deal scoring, LTV, or
// recommendations, so those fields have no replacement here (Principle D,
// delete-not-hide; see task-8-report.md for the product-visible callout).
interface FeeItemized {
  name: string;
  amount: string;
  taxable: boolean;
}

interface DealPreviewResponse {
  amount_financed: number;
  payment_amount: number;
  sales_tax: number;
  fees_total: number;
  fees_itemized: FeeItemized[];
  finance_charge: number;
  total_of_payments: number;
  total_sale_price: number;
  apr: number | null;
  term: number | null;
  payment_frequency: string | null;
  payment_count: number;
  out_the_door: number;
  balance_due: number;
  tax_treatment: string;
  tax_rate_applied: number;
  export_tax_deposit: number;
  due_at_delivery: number;
}

type TaxTreatment = 'standard' | 'out_of_state' | 'export';

interface TradeInLookupResponse {
  estimated_value: number;
  kbb_value?: number;
  edmunds_value?: number;
  confidence_level: string;
  valuation_source: string;
}

interface DealCalculation {
  vehiclePrice: number;
  tradeInValue: number;
  downPayment: number;
  monthlyPayment: number;

  // Auto-calculated fields (sourced from the canonical preview endpoint,
  // POST /deals/calculate -- WS3 Task 8; the browser no longer computes these)
  amountFinanced: number;
  salesTax: number;
  dealerFees: number;
  totalAmountFinanced: number;
  interestRate: number;
  term: number;
  totalOfPayments: number;
  // The TILA finance charge -- every dollar of interest paid over the life of the loan.
  // amount_financed + financeCharge == totalOfPayments, exactly.
  financeCharge: number;
  feesItemized?: FeeItemized[];
  // Cash-deal figures. A deposit holds the car; it reduces what is still owed but never
  // the sales tax (owner ruling 2026-07-12).
  depositAmount: number;
  outTheDoor: number;
  balanceDue: number;
  // Why the sales tax is what it is. Standard = the dealer's own configured rate; an
  // out-of-state or export sale is not taxed at that rate (see core/tax_treatment.py).
  taxTreatment: TaxTreatment;
  taxDestinationState: string;
  taxRateOverride: number | null;
  taxExemptionReference: string;
  taxRateApplied: number;
  // The tax an export sale WOULD have carried, held until the buyer produces the Bill of Lading.
  // Refundable -- it is not sales tax and it is not revenue.
  exportTaxDeposit: number;
  dueAtDelivery: number;
  // How many payments the term actually works out to at the chosen frequency. Distinct from
  // `term`, which is the dealer's term in MONTHS.
  paymentCount: number;
}

interface TradeInLookup {
  vin: string;
  year: number;
  make: string;
  model: string;
  mileage: number;
  condition: 'excellent' | 'good' | 'fair' | 'poor';
  estimatedValue: number;
  kbbValue?: number;
  edmundsValue?: number;
}

// Fee interfaces for Settings integration
interface StandardFee {
  id: number;
  label: string;
  value: string;
  visible: boolean;
}

interface CustomFee {
  id: number;
  name: string;
  value: string;
  type: 'fixed' | 'percentage';
  visible: boolean;
}

interface DealSummaryFinancingTerms {
  downPaymentType: 'fixed' | 'percentage';
  downPaymentValue: string;
  interestRate: string;
  term: string;
  loanOriginationFee: string;
}

interface DealSummarySection {
  standardFees?: StandardFee[];
  customFees?: CustomFee[];
  financingTerms?: DealSummaryFinancingTerms;
}

interface DealSummarySettings {
  bhph: DealSummarySection;
  institutional: DealSummarySection;
  cash: DealSummarySection;
}

// Vehicle and Customer interfaces
interface Vehicle {
  id: string;
  vin: string;
  year: number;
  make: string;
  model: string;
  asking_price: number;
  // A car can carry its OWN minimum down payment -- some cars need more money down. The AI sales
  // assistant already quotes this to customers; the calculator must agree with it.
  bhph_min_down_type?: string | null;
  bhph_min_down_value?: number | null;
  stock_number: string;
}

// A vehicle the customer has expressed interest in, as returned by the
// customer search endpoint (`interested_vehicles[]`). `id` is the vehicle id;
// `priority_order` ranks the list (lower = higher priority, see note in
// handleCustomerSelect about the field being unreliable in legacy rows).
interface InterestedVehicle {
  id: string;
  year?: number;
  make?: string;
  model?: string;
  asking_price?: number;
  priority_order?: number;
  primary_image?: string;
}

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  interested_vehicles?: InterestedVehicle[];
  credit_score?: number;
  monthly_income?: number;
}

const DealCalculatorPage: React.FC = () => {
  const location = useLocation();
  const preSelectedVehicle = location.state?.selectedVehicle;

  const [calculation, setCalculation] = useState<Partial<DealCalculation>>({
    vehiclePrice: 0,
    tradeInValue: 0,
    downPayment: 0,
    monthlyPayment: 0,
    salesTax: 0,
    dealerFees: 1200, // Default dealer fees
    interestRate: 12.5, // Default BHPH rate
    term: 36, // Default 3 years
    depositAmount: 0,
    taxTreatment: 'standard',
    taxDestinationState: '',
    taxRateOverride: null,
    taxExemptionReference: '',
  });

  const [tradeInLookup, setTradeInLookup] = useState<Partial<TradeInLookup>>({});
  const [showTradeInModal, setShowTradeInModal] = useState(false);
  const [calculating, setCalculating] = useState(false);
  // The engine refuses an out-of-state/export deal that hasn't been justified (no destination
  // state, no Bill of Lading). That refusal has to reach the dealer, not just the console.
  const [taxError, setTaxError] = useState<string | null>(null);
  const [stateRates, setStateRates] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [savedDealId, setSavedDealId] = useState<string | null>(null);
  const [showBillOfSaleDialog, setShowBillOfSaleDialog] = useState(false);
  const [billOfSaleName, setBillOfSaleName] = useState('');

  // Settings Integration - Load from localStorage (connected to Settings page)
  const [dealSettings, setDealSettings] = useState({
    standardFees: [
      { id: 1, label: 'Temporary Tag Fee', value: '25', visible: true },
      { id: 2, label: 'DMV Tag/Title Transfer Fee', value: '75', visible: true },
      { id: 3, label: 'Inspection Fee', value: '50', visible: true },
      { id: 4, label: 'Lien Recording Fee', value: '15', visible: false },
    ] as StandardFee[],
    customFees: [
      { id: 1, name: 'Documentation Fee', value: '399', type: 'fixed', visible: true },
      { id: 2, name: 'Processing Fee', value: '199', type: 'fixed', visible: true },
    ] as CustomFee[],
    integrationOptions: {
      allowVehicleSelection: true,
      allowCustomerSelection: true,
      enableSMS: true,
      enableEmail: true,
    },
  });

  const [savedSettings, setSavedSettings] = useState<DealSummarySettings | null>(null);

  // Vehicle and Customer Selection
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [vehicleDropdownOptions, setVehicleDropdownOptions] = useState<StableDropdownOption[]>([]);
  // The selected customer's interested vehicles, rendered as an explicit
  // clickable list so the salesperson can pick the "Sell Vehicle" from the
  // prospect's shortlist (Jose's create-deal flow). The full-inventory search
  // below remains available for selling a non-interested vehicle.
  const [interestedVehicles, setInterestedVehicles] = useState<InterestedVehicle[]>([]);

  // Handle pre-selected vehicle from navigation
  useEffect(() => {
    if (preSelectedVehicle) {
      const vehicle = {
        id: preSelectedVehicle.id,
        vin: preSelectedVehicle.vin,
        year: preSelectedVehicle.year,
        make: preSelectedVehicle.make,
        model: preSelectedVehicle.model,
        asking_price: preSelectedVehicle.askingPrice,
      } as Vehicle;

      setSelectedVehicle(vehicle);
      setCalculation((prev) => ({
        ...prev,
        vehiclePrice: preSelectedVehicle.askingPrice || 0,
      }));

      // Also populate dropdown options with the pre-selected vehicle
      setVehicleDropdownOptions([
        {
          value: preSelectedVehicle.id,
          text: `${preSelectedVehicle.year} ${preSelectedVehicle.make} ${preSelectedVehicle.model}`,
          subtitle: `$${preSelectedVehicle.askingPrice?.toLocaleString() || '0'}`,
          data: vehicle,
        },
      ]);
    }
  }, [preSelectedVehicle]);

  // Deal Type Selection (Cash vs Financing vs BHPH)
  const [dealType, setDealType] = useState<'cash' | 'financing' | 'bhph'>('cash');
  // A cash deal has no loan: its summary shows Out-the-Door / Deposit / Balance Due rather
  // than Total Financed / Monthly Payment / Total of Payments (which rendered as $0).
  const isCashDeal = dealType === 'cash';
  const [paymentFrequency, setPaymentFrequency] = useState<'monthly' | 'biweekly' | 'weekly'>(
    'monthly'
  );

  const termQuickPicks = TERM_QUICK_PICKS[dealType];
  const defaultTermMonths = DEFAULT_TERM_MONTHS[dealType];

  // BHPH is for local buyers only (Jose 2026-07-12): the dealer carries the paper and has to be
  // able to repossess, which fails for a buyer registering out of state or shipping the car
  // overseas. Switching to BHPH therefore forces the tax treatment back to standard -- otherwise
  // an export treatment could ride along silently into a deal that cannot legally be one.
  useEffect(() => {
    if (dealType !== 'bhph') return;
    setCalculation((prev) =>
      prev.taxTreatment === 'standard'
        ? prev
        : {
            ...prev,
            taxTreatment: 'standard',
            taxDestinationState: '',
            taxRateOverride: null,
            taxExemptionReference: '',
          }
    );
  }, [dealType]);

  // Switching deal type must re-default the term. Otherwise moving Financing (60 months) ->
  // BHPH silently leaves a 60-month BHPH contract, which no BHPH dealer writes.
  useEffect(() => {
    if (dealType === 'cash') return;
    setCalculation((prev) => ({ ...prev, term: DEFAULT_TERM_MONTHS[dealType] }));
  }, [dealType]);

  // The payment shown is ONE PERIOD at the selected frequency. Hard-coding "Monthly" made a
  // bi-weekly deal's payment read as a monthly one -- the same number under the wrong word.
  const paymentPeriodLabel =
    paymentFrequency === 'weekly'
      ? 'Weekly'
      : paymentFrequency === 'biweekly'
        ? 'Bi-Weekly'
        : 'Monthly';

  // Reference state tax rates -- a STARTING POINT for an out-of-state sale. The dealer confirms
  // (and may edit) the rate; whatever they confirm is what gets charged.
  useEffect(() => {
    apiService
      .get<Record<string, number>>('/deals/tax-rates/states')
      .then(setStateRates)
      .catch(() => setStateRates({}));
  }, []);

  // Load saved settings on component mount
  useEffect(() => {
    const loadDealSummarySettings = async () => {
      try {
        const settings = (await apiService.get('/settings/deal-summary')) as DealSummarySettings;
        if (settings) {
          setSavedSettings(settings);

          // Update dealSettings with appropriate tab data based on current dealType
          const currentTabSettings = dealType === 'bhph' ? settings.bhph : settings.institutional;
          if (currentTabSettings) {
            setDealSettings((prev) => ({
              ...prev,
              standardFees: currentTabSettings.standardFees || prev.standardFees,
              customFees: currentTabSettings.customFees || prev.customFees,
            }));
          }

          localStorage.setItem('dealSummarySettings', JSON.stringify(settings));
          return;
        }
      } catch (error) {
        console.warn(
          '⚠️ Failed to load deal summary settings from API. Falling back to local storage.',
          error
        );
      }

      const savedSettingsData = localStorage.getItem('dealSummarySettings');
      if (savedSettingsData) {
        try {
          const settings = JSON.parse(savedSettingsData) as DealSummarySettings;
          setSavedSettings(settings);

          // Update dealSettings with appropriate tab data based on current dealType
          const currentTabSettings = dealType === 'bhph' ? settings.bhph : settings.institutional;
          if (currentTabSettings) {
            setDealSettings((prev) => ({
              ...prev,
              standardFees: currentTabSettings.standardFees || prev.standardFees,
              customFees: currentTabSettings.customFees || prev.customFees,
            }));
          }
        } catch (error) {
          console.error('Error loading deal calculator settings:', error);
        }
      }
    };

    loadDealSummarySettings();
  }, [dealType]);

  // Calculate minimum down payment from settings
  /**
   * Is this a PERCENTAGE of the sale price, or a flat dollar amount?
   *
   * The two stores spell it differently -- Settings says 'percentage', a vehicle row says
   * 'percent' -- and the old code tested `=== 'percentage'` with a plain `else` meaning dollars.
   * Feed a vehicle's 'percent' through that and a 20% minimum down silently becomes a $20
   * minimum down. Same bug class as the $299 fee and the 5%-charged-as-$5 fee: never let an
   * unrecognized unit fall through to a default that changes the money.
   */
  const isPercentType = (type: string | null | undefined): boolean => {
    const t = (type ?? '').trim().toLowerCase();
    return t === 'percentage' || t === 'percent' || t === '%';
  };

  const getMinimumDownPayment = useCallback(
    (
      vehiclePrice: number,
      settings: DealSummarySettings | null,
      vehicle: Vehicle | null
    ): number => {
      if (!vehiclePrice) return 0;

      // The vehicle's own minimum WINS (Jose 2026-07-12: "inventory hardcoded wins"). A per-car
      // minimum exists precisely because some cars need more down, and the AI assistant is
      // already telling customers this number. Settings is the fallback for cars that don't
      // carry one.
      if (vehicle?.bhph_min_down_value != null && vehicle.bhph_min_down_type) {
        return isPercentType(vehicle.bhph_min_down_type)
          ? vehiclePrice * (vehicle.bhph_min_down_value / 100)
          : vehicle.bhph_min_down_value;
      }

      if (!settings) return 0;
      const currentTabSettings = dealType === 'bhph' ? settings.bhph : settings.institutional;
      if (!currentTabSettings?.financingTerms) return 0;

      const { downPaymentType, downPaymentValue } = currentTabSettings.financingTerms;
      return isPercentType(downPaymentType)
        ? vehiclePrice * (parseFloat(downPaymentValue) / 100)
        : parseFloat(downPaymentValue) || 0;
    },
    [dealType]
  );

  const minimumDown = getMinimumDownPayment(
    calculation.vehiclePrice || 0,
    savedSettings,
    selectedVehicle
  );
  // Which store the minimum came from -- the vehicle's own setting overrides the dealership
  // default, and the salesperson should be told so rather than left to wonder.
  const minimumDownSource: 'vehicle' | 'settings' =
    selectedVehicle?.bhph_min_down_value != null && selectedVehicle.bhph_min_down_type
      ? 'vehicle'
      : 'settings';

  // Update finance terms and fees when dealType changes
  useEffect(() => {
    // Only log and process if we actually have savedSettings (prevent double execution)
    if (!savedSettings) return;

    const currentTabSettings =
      dealType === 'bhph' ? savedSettings.bhph : savedSettings.institutional;
    if (currentTabSettings) {
      // Update dealSettings (fees)
      setDealSettings((prev) => ({
        ...prev,
        standardFees: currentTabSettings.standardFees || prev.standardFees,
        customFees: currentTabSettings.customFees || prev.customFees,
      }));

      // Update calculation (finance terms)
      const financingTerms = currentTabSettings.financingTerms;
      if (financingTerms) {
        const minimumDownPayment = getMinimumDownPayment(
          calculation.vehiclePrice || 0,
          savedSettings,
          selectedVehicle
        );
        setCalculation((prev) => ({
          ...prev,
          interestRate:
            parseFloat(financingTerms.interestRate) || (dealType === 'bhph' ? 18.9 : 12.5),
          term: parseInt(financingTerms.term) || (dealType === 'bhph' ? 24 : 60),
          // Set minimum down payment when switching to financing/bhph, clear when switching to cash
          downPayment:
            dealType === 'cash'
              ? 0
              : calculation.vehiclePrice
                ? minimumDownPayment
                : prev.downPayment || minimumDownPayment || 0,
        }));
      } else {
        // Set defaults if no settings exist
        setCalculation((prev) => ({
          ...prev,
          interestRate: dealType === 'bhph' ? 18.9 : 12.5,
          term: dealType === 'bhph' ? 24 : 60,
          downPayment:
            dealType === 'cash'
              ? 0
              : dealType === 'bhph'
                ? (calculation.vehiclePrice || 0) * 0.2
                : 1200, // 20% for BHPH, $1200 for financing, $0 for cash
        }));
      }
    }
    // selectedVehicle belongs here: the minimum down can come from the VEHICLE's own setting, so
    // picking a different car must re-derive it. Without the dep it would keep the previous car's
    // minimum -- which is exactly the "inventory hardcoded wins" rule silently not winning.
  }, [dealType, savedSettings, calculation.vehiclePrice, getMinimumDownPayment, selectedVehicle]);

  // WS3 Task 8: THE ONE deal preview endpoint (POST /deals/calculate). Replaces
  // both the local exponentiation-based amortization useEffect (former E4-style duplicate)
  // and the calls to the now-deleted E3 (/calculations/deal-structure-enhanced).
  // The canonical engine computes tax/fees from the tenant's configured
  // deal_calculation_settings — dealSettings/savedSettings (the separate
  // /settings/deal-summary fee editor) is no longer summed into the request or
  // the displayed totals; its itemized rows are replaced by the response's
  // fees_itemized (see the Deal Summary panel below).
  const fetchDealPreview = useCallback(
    async (params: {
      vehiclePrice: number;
      tradeInValue: number;
      downPayment: number;
      interestRate: number;
      term: number;
      dealTypeValue: 'cash' | 'financing' | 'bhph';
      frequency: 'monthly' | 'biweekly' | 'weekly';
      depositAmount: number;
      taxTreatment: TaxTreatment;
      taxDestinationState: string;
      taxRateOverride: number | null;
      taxExemptionReference: string;
      vehicleId?: string;
      customerId?: string;
    }) => {
      if (!params.vehiclePrice) return;

      setCalculating(true);
      try {
        const isCash = params.dealTypeValue === 'cash';
        const preview = await apiService.post<DealPreviewResponse>('/deals/calculate', {
          vehicle_price: params.vehiclePrice,
          trade_in_allowance: params.tradeInValue || 0,
          // On a cash deal the money down IS the deposit. Safe to send as down_payment:
          // it reduces the balance owed, not the tax base (TaxBaseRule.PRICE_MINUS_TRADE).
          down_payment: isCash ? params.depositAmount || 0 : params.downPayment || 0,
          apr: isCash ? undefined : params.interestRate,
          // A term in MONTHS. The backend converts it to a payment count for the chosen
          // frequency (24 months bi-weekly = 52 payments, not 24). Sending it as
          // term_periods built a loan half the intended length and doubled the payment.
          term_months: isCash ? undefined : params.term || undefined,
          frequency: isCash ? undefined : params.frequency,
          // dealType decision (WS3 Task 8, see task-8-report.md): kept the
          // existing 'cash'|'financing'|'bhph' union as-is rather than
          // migrating every call site to canonical labels — the backend's
          // existing alias table (api/v1/endpoints/deal_contracting.py::
          // _normalize_deal_type, reused by the preview endpoint) already maps
          // 'financing' -> outside_finance.
          deal_type: params.dealTypeValue,
          tax_treatment: params.taxTreatment,
          tax_destination_state: params.taxDestinationState || undefined,
          tax_rate_override: params.taxRateOverride ?? undefined,
          tax_exemption_reference: params.taxExemptionReference || undefined,
          vehicle_id: params.vehicleId,
          customer_id: params.customerId,
        });

        setCalculation((prev) => ({
          ...prev,
          amountFinanced: preview.amount_financed,
          salesTax: preview.sales_tax,
          dealerFees: preview.fees_total,
          totalAmountFinanced: preview.amount_financed,
          totalOfPayments: preview.total_of_payments,
          financeCharge: preview.finance_charge,
          interestRate: preview.apr ?? prev.interestRate,
          // NOT `term: preview.term` -- calculation.term is the dealer's term in MONTHS, while
          // the response echoes the PAYMENT COUNT (24 months bi-weekly -> 52). Writing that back
          // into the months dropdown would re-lengthen the loan on every keystroke.
          paymentCount: preview.payment_count,
          monthlyPayment: preview.payment_amount,
          feesItemized: preview.fees_itemized,
          outTheDoor: preview.out_the_door,
          balanceDue: preview.balance_due,
          taxRateApplied: preview.tax_rate_applied,
          exportTaxDeposit: preview.export_tax_deposit,
          dueAtDelivery: preview.due_at_delivery,
        }));
        setTaxError(null);
      } catch (error) {
        const message = (error as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        // A 422 here is the engine refusing an unjustified tax treatment -- an actionable
        // message for the dealer ("record the Bill of Lading"), not a console line.
        setTaxError(message ?? null);
        console.error('Calculation error:', error);
      } finally {
        setCalculating(false);
      }
    },
    []
  );

  // Debounced auto-preview: fires ~500ms after the last relevant input change.
  // Requirement (WS3 Task 8, Task 6 dependency): paymentFrequency is now sent
  // so bi-weekly/weekly deals actually reach the engine instead of silently
  // defaulting to monthly.
  const previewDebounceKey = useDebounce(
    calculation.vehiclePrice
      ? [
          calculation.vehiclePrice,
          calculation.tradeInValue,
          calculation.downPayment,
          calculation.depositAmount,
          calculation.taxTreatment,
          calculation.taxDestinationState,
          calculation.taxRateOverride,
          calculation.taxExemptionReference,
          calculation.interestRate,
          calculation.term,
          dealType,
          paymentFrequency,
          selectedVehicle?.id ?? '',
          selectedCustomer?.id ?? '',
        ].join('|')
      : null,
    500
  );

  useEffect(() => {
    if (!previewDebounceKey) return;
    fetchDealPreview({
      vehiclePrice: calculation.vehiclePrice || 0,
      tradeInValue: calculation.tradeInValue || 0,
      downPayment: calculation.downPayment || 0,
      depositAmount: calculation.depositAmount || 0,
      taxTreatment: calculation.taxTreatment || 'standard',
      taxDestinationState: calculation.taxDestinationState || '',
      taxRateOverride: calculation.taxRateOverride ?? null,
      taxExemptionReference: calculation.taxExemptionReference || '',
      interestRate: calculation.interestRate || 0,
      term: calculation.term || 0,
      dealTypeValue: dealType,
      frequency: paymentFrequency,
      vehicleId: selectedVehicle?.id,
      customerId: selectedCustomer?.id,
    });
    // fetchDealPreview is stable (empty dep array); only the debounced
    // snapshot should re-trigger the fetch, not every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDebounceKey]);

  const lookupTradeInValue = async () => {
    if (
      !tradeInLookup.vin &&
      (!tradeInLookup.year || !tradeInLookup.make || !tradeInLookup.model)
    ) {
      toast.error('Please enter either a VIN or Year/Make/Model');
      return;
    }

    setCalculating(true);
    try {
      const tradeValue = await apiService.post<TradeInLookupResponse>(
        '/api/v1/calculations/trade-value',
        tradeInLookup
      );

      setTradeInLookup((prev) => ({
        ...prev,
        estimatedValue: tradeValue.estimated_value,
        kbbValue: tradeValue.kbb_value,
        edmundsValue: tradeValue.edmunds_value,
      }));

      // Auto-fill trade-in value in calculator
      setCalculation((prev) => ({
        ...prev,
        tradeInValue: tradeValue.estimated_value,
      }));
    } catch (error) {
      console.error('Trade-in lookup error:', error);
    } finally {
      setCalculating(false);
    }
  };

  const saveDealCalculation = async () => {
    setSaving(true);
    try {
      const response = await apiService.post<{ status: string; deal_id: string; message: string }>(
        '/deal-contracting/from-calculation',
        {
          calculation,
          deal_type: dealType,
          // WS3 Task 8 (Task 6 dependency): send the selected payment frequency
          // so bi-weekly/weekly deals actually flow to the engine instead of
          // silently defaulting to monthly (api/v1/endpoints/deal_contracting.py
          // ::create_deal_from_calculation).
          payment_frequency: paymentFrequency,
          trade_in_details: tradeInLookup,
          customer_id: selectedCustomer?.id,
          vehicle_id: selectedVehicle?.id,
        }
      );

      if (response && response.deal_id) {
        setSavedDealId(response.deal_id);
        toast.success('Deal calculation saved successfully! You can now generate contracts.');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Error saving deal calculation');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateDocument = async (_documentName?: string) => {
    if (!savedDealId) return;
    try {
      // Downloads through the authenticated API client (window.open won't pass
      // the auth header). The shared helper names the file deal-{id}-{type}.pdf;
      // the dialog's custom name is currently not applied (WS3-3b follow-up).
      await downloadDealDocuments(savedDealId);
    } catch (e) {
      console.error('PDF generation failed', e);
      toast.error('Failed to generate PDF');
    }
  };

  const openBillOfSaleDialog = () => {
    if (!savedDealId) return;
    const customerName = selectedCustomer
      ? `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim()
      : '';
    const vehicleName = selectedVehicle
      ? `${selectedVehicle.year || ''} ${selectedVehicle.make || ''} ${selectedVehicle.model || ''}`.trim()
      : '';
    const baseNameParts = [
      customerName || 'Customer',
      vehicleName || 'Vehicle',
      'Bill of Sale',
    ].filter(Boolean);
    setBillOfSaleName(baseNameParts.join(' - '));
    setShowBillOfSaleDialog(true);
  };

  const printCalculation = () => {
    window.print();
  };

  // Search functions for dropdown components
  const searchCustomers = async (query: string): Promise<StableDropdownOption[]> => {
    if (!query.trim()) {
      return [];
    }

    setCustomerLoading(true);

    try {
      // Search customers with clean database - no corruption handling needed
      const response = await apiService.get<Record<string, unknown>>(
        buildCustomerSearchEndpoint(query)
      );

      // Handle both array response and object with data property
      const customers = Array.isArray(response)
        ? response
        : (((response as Record<string, unknown>).data ||
            (response as Record<string, unknown>).customers ||
            []) as Record<string, unknown>[]);

      if (!Array.isArray(customers)) {
        console.warn('Customer search response is not an array:', customers);
        return [];
      }

      // Simple mapping since database is now clean
      const options: StableDropdownOption[] = customers
        .map((customer: Record<string, unknown>) => {
          const customerName =
            `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown Customer';
          const customerPhone = (customer.phone as string) || '';

          return {
            value:
              (customer.id as string)?.toString() ||
              (customer.customer_id as string)?.toString() ||
              '',
            text: customerName,
            subtitle: customerPhone,
            data: customer,
          };
        })
        .filter((option) => option.text !== 'Unknown Customer' && option.text.trim().length > 0);

      return options;
    } catch (error) {
      console.error('Error searching customers:', error);
      return [];
    } finally {
      setCustomerLoading(false);
    }
  };

  const searchVehicles = async (query: string): Promise<StableDropdownOption[]> => {
    if (!query.trim()) {
      return [];
    }

    setVehicleLoading(true);

    try {
      const response = await apiService.get<Record<string, unknown>>(
        buildVehicleSearchEndpoint(query)
      );

      // Handle both array response and object with data property
      const vehicles = Array.isArray(response)
        ? response
        : (((response as Record<string, unknown>).data ||
            (response as Record<string, unknown>).vehicles ||
            []) as Record<string, unknown>[]);

      if (!Array.isArray(vehicles)) {
        console.warn('Vehicle search response is not an array:', vehicles);
        return [];
      }

      const options: StableDropdownOption[] = vehicles.map((vehicle: Record<string, unknown>) => ({
        value: (vehicle.id as string)?.toString() || '',
        text:
          `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() ||
          'Unknown Vehicle',
        subtitle: `$${(vehicle.asking_price as number)?.toLocaleString() || '0'} • Stock: ${(vehicle.stock_number as string) || 'N/A'}`,
        data: vehicle,
      }));

      setVehicleDropdownOptions(options); // Update dropdown options
      return options;
    } catch (error) {
      console.error('Error searching vehicles:', error);
      return [];
    } finally {
      setVehicleLoading(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (value: string, option: StableDropdownOption) => {
    if (value) {
      const customer = option.data as Customer | undefined;
      setSelectedCustomer(customer ?? null);

      // Pre-fill credit info from customer
      setCalculation((prev) => ({
        ...prev,
      }));

      // Surface the customer's interested vehicles as an explicit pick-list.
      // Lowest priority_order first (1 = highest); legacy rows may be 0, so
      // treat missing/0 as lowest-priority via the nullish fallback.
      const interested = [...(customer?.interested_vehicles ?? [])].sort(
        (a, b) =>
          (a.priority_order || Number.MAX_SAFE_INTEGER) -
          (b.priority_order || Number.MAX_SAFE_INTEGER)
      );
      setInterestedVehicles(interested);

      // Pre-select the top interested vehicle as the default "Sell Vehicle".
      // Switching customers is always intentional on a create-deal surface, so
      // we re-default to the new customer's #1 (clobbering a prior auto-pick).
      // The only override is a vehicle navigated in with (preSelectedVehicle).
      if (!preSelectedVehicle && interested.length > 0) {
        selectInterestedVehicle(interested[0]);
      }
    } else {
      setSelectedCustomer(null);
      setInterestedVehicles([]);
    }
  };

  // Pick one of the customer's interested vehicles as the sell vehicle.
  const selectInterestedVehicle = (iv: InterestedVehicle) => {
    setSelectedVehicle({
      id: iv.id,
      vin: '',
      year: iv.year ?? 0,
      make: iv.make ?? '',
      model: iv.model ?? '',
      asking_price: iv.asking_price ?? 0,
      stock_number: '',
    });
    setCalculation((prev) => ({ ...prev, vehiclePrice: iv.asking_price || 0 }));
  };

  // Handle vehicle selection
  const handleVehicleSelect = (value: string, option: StableDropdownOption) => {
    if (value) {
      const vehicle = option.data as Vehicle | undefined;
      setSelectedVehicle(vehicle ?? null);
      setCalculation((prev) => ({
        ...prev,
        vehiclePrice: vehicle?.asking_price || 0,
      }));
    } else {
      setSelectedVehicle(null);
      setCalculation((prev) => ({
        ...prev,
        vehiclePrice: 0,
      }));
    }
  };

  return (
    <FlexibleContainer size="full" padding="minimal" adaptToSidebar>
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center mb-3">
          <CalculatorIcon className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Deal Calculator</h1>
        </div>
        <p className="text-gray-600">
          Calculate deal structure with trade-in values. Perfect for BHPH and cash deals.
        </p>
      </div>

      {showBillOfSaleDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Name your Bill of Sale</h2>
              <button
                onClick={() => setShowBillOfSaleDialog(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-600">Choose a file name for the PDF download.</p>

            <label className="mt-4 block text-xs font-medium text-gray-700">File name</label>
            <input
              type="text"
              value={billOfSaleName}
              onChange={(event) => setBillOfSaleName(event.target.value)}
              placeholder="Customer - Vehicle - Bill of Sale"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowBillOfSaleDialog(false)}
                className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowBillOfSaleDialog(false);
                  handleGenerateDocument(billOfSaleName);
                }}
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <AdaptiveGrid
        minItemWidth={320}
        maxCols={2}
        gap="sm"
        responsive={{ mobile: 1, tablet: 2, desktop: 2 }}
      >
        {/* INPUT SECTION */}
        <div className="space-y-2">
          {/* Customer Selection (if enabled) */}
          {dealSettings.integrationOptions.allowCustomerSelection && (
            <CollapsibleSection
              title={
                <div className="flex items-center">
                  <UserIcon className="h-6 w-6 text-purple-600 mr-2" />
                  Search Customer (Existing or Walk-in)
                </div>
              }
              variant="bordered"
              defaultExpanded={true}
              persistState
              storageKey="deal-calculator-customer"
            >
              <StableDropdown
                options={[]}
                value={selectedCustomer?.id || ''}
                onSelect={handleCustomerSelect}
                onSearch={searchCustomers}
                placeholder="Type to search customer by name or phone..."
                loading={customerLoading}
                className="w-full"
                hideChevron={true}
              />
            </CollapsibleSection>
          )}

          {/* Vehicle Selection (if enabled) */}
          {dealSettings.integrationOptions.allowVehicleSelection && (
            <CollapsibleSection
              title={
                <div className="flex items-center">
                  <TruckIcon className="h-6 w-6 text-green-600 mr-2" />
                  Search Active Inventory
                </div>
              }
              variant="bordered"
              defaultExpanded={true}
              persistState
              storageKey="deal-calculator-vehicle"
            >
              {interestedVehicles.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-300 mb-1.5">
                    {selectedCustomer?.first_name || 'Customer'}'s interested vehicles — pick one to
                    sell:
                  </p>
                  <div className="space-y-1.5">
                    {interestedVehicles.map((iv) => {
                      const isSelected = selectedVehicle?.id === iv.id;
                      return (
                        <button
                          type="button"
                          key={iv.id}
                          onClick={() => selectInterestedVehicle(iv)}
                          className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-md border transition-colors ${
                            isSelected
                              ? 'border-green-500 bg-green-50 ring-1 ring-green-500'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                        >
                          <span className="text-sm text-gray-900">
                            {`${iv.year || ''} ${iv.make || ''} ${iv.model || ''}`.trim() ||
                              'Unknown Vehicle'}
                          </span>
                          <span className="text-sm font-medium text-gray-700">
                            ${iv.asking_price?.toLocaleString() || '0'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">
                    Or search active inventory below to sell a different vehicle.
                  </p>
                </div>
              )}
              <StableDropdown
                options={vehicleDropdownOptions}
                value={selectedVehicle?.id || ''}
                onSelect={handleVehicleSelect}
                onSearch={searchVehicles}
                placeholder="Type to search vehicle by year, make, model, or stock..."
                loading={vehicleLoading}
                className="w-full"
                hideChevron={true}
              />
            </CollapsibleSection>
          )}

          {/* Deal Type Selection */}
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center mb-2">
              <CalculatorIcon className="h-5 w-5 text-indigo-600 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900">Deal Type</h3>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-2">
              <label
                className={`flex items-center justify-center p-2 border rounded-md cursor-pointer transition-colors ${dealType === 'cash' ? 'bg-green-50 border-green-300' : 'border-gray-300 hover:bg-gray-50'}`}
              >
                <input
                  type="radio"
                  name="dealType"
                  value="cash"
                  checked={dealType === 'cash'}
                  onChange={(e) => setDealType(e.target.value as 'cash' | 'financing' | 'bhph')}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="text-lg mb-1">💵</div>
                  <div className="text-xs font-medium text-gray-700">Cash</div>
                </div>
              </label>

              <label
                className={`flex items-center justify-center p-2 border rounded-md cursor-pointer transition-colors ${dealType === 'financing' ? 'bg-blue-50 border-blue-300' : 'border-gray-300 hover:bg-gray-50'}`}
              >
                <input
                  type="radio"
                  name="dealType"
                  value="financing"
                  checked={dealType === 'financing'}
                  onChange={(e) => setDealType(e.target.value as 'cash' | 'financing' | 'bhph')}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="text-lg mb-1">🏦</div>
                  <div className="text-xs font-medium text-gray-700">Financing</div>
                </div>
              </label>

              <label
                className={`flex items-center justify-center p-2 border rounded-md cursor-pointer transition-colors ${dealType === 'bhph' ? 'bg-purple-50 border-purple-300' : 'border-gray-300 hover:bg-gray-50'}`}
              >
                <input
                  type="radio"
                  name="dealType"
                  value="bhph"
                  checked={dealType === 'bhph'}
                  onChange={(e) => setDealType(e.target.value as 'cash' | 'financing' | 'bhph')}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="text-lg mb-1">🤝</div>
                  <div className="text-xs font-medium text-gray-700">BHPH</div>
                </div>
              </label>
            </div>

            <p className="text-xs text-gray-500 text-center">
              {dealType === 'cash'
                ? 'Full upfront payment'
                : dealType === 'financing'
                  ? 'External lender financing'
                  : 'In-house financing'}
            </p>
          </div>

          {/* Customer Credit Profile REMOVED 2026-07-12 (Jose): the Calculator is a quote tool --
              the real deal process lives under Customer Deals. Its Credit Score / Monthly Gross
              Income inputs also fed nothing: WS3 deleted the advisory panel they drove, so they
              collected data and produced no output. WS3 Plan 2 rebuilds advisory on the deal page. */}

          {/* BHPH / Financing Terms sits ABOVE pricing (Jose 2026-07-12): on a BHPH deal the
              rate/term/frequency are what the dealer sets first -- they drive every number
              below them. Cash deals have no terms section, so pricing stays first there. */}
          {/* Tax Treatment. The engine's rate is right for almost every deal -- a buyer who
              registers the car in the dealer's own state. Two cases genuinely are not, and both
              are real for UAB: an out-of-state buyer (taxed at THEIR state's rate) and an
              exported vehicle (exempt, but the Bill of Lading has to be on record). Deliberately
              NOT a free-text tax dollar amount: a routinely-used override means the engine is
              wrong and should be fixed, not worked around (Jose 2026-07-12). */}
          <CollapsibleSection
            title="Sales Tax Treatment"
            variant="bordered"
            defaultExpanded={calculation.taxTreatment !== 'standard'}
            persistState
            storageKey="deal-calculator-tax-treatment"
          >
            <FormFieldGroup layout="horizontal" spacing="normal" equalWidth>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Taxed as</label>
                <select
                  value={calculation.taxTreatment || 'standard'}
                  onChange={(e) => {
                    const treatment = e.target.value as TaxTreatment;
                    setCalculation((prev) => ({
                      ...prev,
                      taxTreatment: treatment,
                      // Clear the other branch's fields so a stale Bill of Lading can't ride along
                      // on an out-of-state deal (or a stale state on an export).
                      taxDestinationState:
                        treatment === 'out_of_state' ? prev.taxDestinationState : '',
                      taxRateOverride:
                        treatment === 'out_of_state' ? (prev.taxRateOverride ?? null) : null,
                      taxExemptionReference:
                        treatment === 'export' ? prev.taxExemptionReference : '',
                    }));
                  }}
                  className="block w-full py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="standard">Standard — our state's rate</option>
                  {/* BHPH is local-only, so these are not offered on a BHPH deal at all. */}
                  {dealType !== 'bhph' && (
                    <>
                      <option value="out_of_state">Out-of-state — buyer's home state rate</option>
                      <option value="export">Export — exempt (shipped overseas)</option>
                    </>
                  )}
                </select>
                {dealType === 'bhph' && (
                  <p className="mt-1 text-xs text-gray-500">
                    BHPH is for local buyers only — an out-of-state or export sale has to be a cash
                    or finance deal.
                  </p>
                )}
                {calculation.taxTreatment === 'standard' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Tax is {calculation.taxRateApplied ?? 0}% of the sale price, from Settings.
                  </p>
                )}
              </div>

              {calculation.taxTreatment === 'out_of_state' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Registered in
                    </label>
                    <select
                      value={calculation.taxDestinationState || ''}
                      onChange={(e) => {
                        const state = e.target.value;
                        // Prefill the reference rate as a STARTING POINT -- the dealer confirms it.
                        const suggested = stateRates[state];
                        setCalculation((prev) => ({
                          ...prev,
                          taxDestinationState: state,
                          taxRateOverride: suggested ?? prev.taxRateOverride ?? null,
                        }));
                      }}
                      className="block w-full py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select state…</option>
                      {Object.keys(stateRates)
                        .sort()
                        .map((st) => (
                          <option key={st} value={st}>
                            {st} ({stateRates[st]}%)
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tax rate</label>
                    <div className="relative">
                      <input
                        type="number"
                        min={0}
                        max={20}
                        step={0.01}
                        value={calculation.taxRateOverride ?? ''}
                        onChange={(e) =>
                          setCalculation((prev) => ({
                            ...prev,
                            taxRateOverride:
                              e.target.value === '' ? null : parseFloat(e.target.value),
                          }))
                        }
                        className="block w-full pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm">%</span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Pre-filled from a reference table — confirm it. Enter 0 if that state has no
                      sales tax.
                    </p>
                  </div>
                </>
              )}

              {calculation.taxTreatment === 'export' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bill of Lading{' '}
                    <span className="font-normal text-gray-500">(when the shipment is booked)</span>
                  </label>
                  <input
                    type="text"
                    maxLength={120}
                    value={calculation.taxExemptionReference || ''}
                    onChange={(e) =>
                      setCalculation((prev) => ({ ...prev, taxExemptionReference: e.target.value }))
                    }
                    placeholder="e.g. B/L 4471-DR"
                    className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {/* NOT required at deal time: the shipping line issues the B/L AFTER the
                      sale, so the buyer cannot produce one yet (Jose 2026-07-12). But the $0 tax
                      is granted on the PROMISE of export -- if the shipment never happens, the
                      dealer owes the state the tax. Say that out loud rather than letting it look
                      like a blank optional field. */}
                  {calculation.taxExemptionReference?.trim() ? (
                    <p className="mt-1 text-xs text-gray-500">
                      Prints on the contract as the reason the tax is $0.
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-amber-700">
                      Leave blank for now — the shipping line issues this after the sale. The deal
                      saves with the exemption <strong>pending</strong>, and the contract will say
                      so. Record it when the shipment is booked: until then the dealership is liable
                      for the tax if the vehicle never leaves the country.
                    </p>
                  )}
                </div>
              )}
            </FormFieldGroup>

            {taxError && (
              <p role="alert" className="mt-3 text-sm text-red-600">
                {taxError}
              </p>
            )}
          </CollapsibleSection>

          {/* Financing Terms - Show for financing and BHPH deals */}
          {(dealType === 'financing' || dealType === 'bhph') && (
            <CollapsibleSection
              title={dealType === 'bhph' ? 'BHPH Terms' : 'Financing Terms'}
              variant="bordered"
              defaultExpanded={true}
              persistState
              storageKey={`deal-calculator-financing-${dealType}`}
            >
              <FormFieldGroup layout="horizontal" spacing="normal" equalWidth>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Interest Rate (APR)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      value={calculation.interestRate || ''}
                      onChange={(e) =>
                        setCalculation((prev) => ({
                          ...prev,
                          interestRate: parseFloat(e.target.value) || 0,
                        }))
                      }
                      className="block w-full pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="12.5"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm">%</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Term (Months)
                  </label>
                  {/* Quick picks + free entry. BHPH is short paper -- dealers write 6/12/18/24
                      month contracts (often to re-assign them on), so the old 24-72 list had no
                      usable BHPH option at all. A datalist keeps any term typeable (9, 15, 30...)
                      instead of locking the dealer to a fixed set. */}
                  <input
                    type="number"
                    min={1}
                    max={84}
                    step={1}
                    list={`term-options-${dealType}`}
                    value={calculation.term ?? defaultTermMonths}
                    onChange={(e) => {
                      const months = parseInt(e.target.value, 10);
                      setCalculation((prev) => ({
                        ...prev,
                        term: Number.isNaN(months) ? 0 : months,
                      }));
                    }}
                    className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <datalist id={`term-options-${dealType}`}>
                    {termQuickPicks.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                  {/* A bi-weekly "24 month" deal is 52 payments, not 24. Show the real count so
                      the term can't be misread as a payment count. */}
                  {paymentFrequency !== 'monthly' && !!calculation.paymentCount && (
                    <p className="text-xs text-gray-500 mt-1">
                      = {calculation.paymentCount} {paymentPeriodLabel.toLowerCase()} payments
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Frequency
                  </label>
                  <select
                    value={paymentFrequency}
                    onChange={(e) =>
                      setPaymentFrequency(e.target.value as 'monthly' | 'biweekly' | 'weekly')
                    }
                    className="block w-full py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="biweekly">Bi-Weekly (26/year)</option>
                    <option value="weekly">Weekly (52/year)</option>
                  </select>
                </div>

                {/* The BHPH Processing Fee (settings: loanOriginationFee) used to render here as a
                    read-only box captioned "automatically included in calculations". It was included
                    in NOTHING -- no code read it. It is now a real fee from Settings, charged and
                    itemized in the Deal Summary with the standard/custom fees (Jose 2026-07-12). */}
              </FormFieldGroup>
            </CollapsibleSection>
          )}

          {/* Vehicle Information */}
          <FormFieldGroup
            title={
              <div className="flex items-center">
                <TruckIcon className="h-6 w-6 text-blue-600 mr-2" />
                Vehicle Price
              </div>
            }
            layout="horizontal"
            spacing="normal"
            border
            equalWidth
          >
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Vehicle Asking Price *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={calculation.vehiclePrice || ''}
                  onChange={(e) => {
                    const newVehiclePrice = parseFloat(e.target.value) || 0;
                    const minimumDownPayment = getMinimumDownPayment(
                      newVehiclePrice,
                      savedSettings,
                      selectedVehicle
                    );
                    setCalculation((prev) => ({
                      ...prev,
                      vehiclePrice: newVehiclePrice,
                      // Always set to minimum down payment when vehicle price changes, but only for financing/bhph deals
                      downPayment:
                        dealType === 'financing' || dealType === 'bhph' ? minimumDownPayment : 0,
                    }));
                  }}
                  className="block w-full pl-8 pr-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="25,000"
                />
              </div>
            </div>

            {(dealType === 'financing' || dealType === 'bhph') && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {paymentPeriodLabel} Payment
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="block w-full pl-8 pr-3 py-2 text-sm font-semibold border border-gray-200 rounded-md bg-gray-50 text-blue-600">
                    {calculation.monthlyPayment
                      ? `$${calculation.monthlyPayment.toFixed(2)}`
                      : '$0.00'}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Auto-calculated</p>
              </div>
            )}
          </FormFieldGroup>

          {/* Trade-In Section */}
          <FormFieldGroup
            title={
              <div className="flex items-center">
                <TruckIcon className="h-6 w-6 text-green-600 mr-2" />
                Trade-In Value
              </div>
            }
            layout="horizontal"
            spacing="normal"
            border
            equalWidth
            actions={
              <button
                onClick={() => setShowTradeInModal(true)}
                className="flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
              >
                <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                Look Up Value
              </button>
            }
          >
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Trade-In Allowance
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={calculation.tradeInValue || ''}
                  onChange={(e) =>
                    setCalculation((prev) => ({
                      ...prev,
                      tradeInValue: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="block w-full pl-8 pr-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="8,500"
                />
              </div>
            </div>

            {(dealType === 'financing' || dealType === 'bhph') && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Down Payment</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    value={calculation.downPayment || ''}
                    onChange={(e) => {
                      const newDownPayment = parseFloat(e.target.value) || 0;
                      setCalculation((prev) => ({
                        ...prev,
                        downPayment: newDownPayment,
                      }));
                    }}
                    className={`block w-full pl-8 pr-3 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      (calculation.downPayment || 0) < minimumDown
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                    }`}
                  />
                </div>
                <div className="mt-1">
                  {!!calculation.vehiclePrice && minimumDown > 0 && (
                    <p className="text-xs text-blue-600">
                      Minimum required: ${minimumDown.toLocaleString()}
                      {/* Say WHERE the number came from. A per-car minimum overriding the
                          dealership default is exactly the sort of thing a salesperson needs to
                          see, not guess at. */}
                      {minimumDownSource === 'vehicle' && (
                        <span className="text-gray-500"> · set on this vehicle</span>
                      )}
                    </p>
                  )}
                  {(calculation.downPayment || 0) < minimumDown && (
                    <p className="text-xs text-red-600">
                      Down payment is below minimum requirement
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Cash deposit — money down to hold the car. Reduces the balance owed, never
                the sales tax. Saving the deal with a deposit takes the vehicle off
                available inventory (status PENDING) so it can't be sold twice. */}
            {isCashDeal && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Deposit (holds the vehicle)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={calculation.depositAmount || ''}
                    onChange={(e) =>
                      setCalculation((prev) => ({
                        ...prev,
                        depositAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="block w-full pl-8 pr-3 py-2 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0"
                  />
                </div>
                {(calculation.depositAmount || 0) > 0 && (
                  <p className="mt-1 text-xs text-amber-700">
                    Saving this deal marks the vehicle as Pending and removes it from available
                    inventory.
                  </p>
                )}
              </div>
            )}
          </FormFieldGroup>

          {/* Calculate Deal Button - Now optional since calculations are automatic */}
          <div className="flex justify-center">
            <button
              onClick={() =>
                fetchDealPreview({
                  vehiclePrice: calculation.vehiclePrice || 0,
                  tradeInValue: calculation.tradeInValue || 0,
                  downPayment: calculation.downPayment || 0,
                  depositAmount: calculation.depositAmount || 0,
                  taxTreatment: calculation.taxTreatment || 'standard',
                  taxDestinationState: calculation.taxDestinationState || '',
                  taxRateOverride: calculation.taxRateOverride ?? null,
                  taxExemptionReference: calculation.taxExemptionReference || '',
                  interestRate: calculation.interestRate || 0,
                  term: calculation.term || 0,
                  dealTypeValue: dealType,
                  frequency: paymentFrequency,
                  vehicleId: selectedVehicle?.id,
                  customerId: selectedCustomer?.id,
                })
              }
              disabled={!calculation.vehiclePrice || calculating}
              className="flex items-center px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {calculating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Calculating...
                </>
              ) : (
                <>
                  <CalculatorIcon className="h-4 w-4 mr-2" />
                  Recalculate Deal
                </>
              )}
            </button>
          </div>
        </div>

        {/* RESULTS SECTION - Right Column */}
        <div className="space-y-2">
          {/* Calculation Summary */}
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Deal Summary</h3>

            <div className="space-y-2">
              <div className="flex justify-between items-center py-1">
                <span className="text-sm text-gray-600">Vehicle Price:</span>
                <span className="text-sm font-semibold">
                  ${calculation.vehiclePrice?.toLocaleString() || '0'}
                </span>
              </div>

              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Trade-In Value:</span>
                <span className="font-semibold text-green-600">
                  -${calculation.tradeInValue?.toLocaleString() || '0'}
                </span>
              </div>

              {(dealType === 'financing' || dealType === 'bhph') && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">Down Payment:</span>
                  <span className="font-semibold text-green-600">
                    -${calculation.downPayment?.toLocaleString() || '0'}
                  </span>
                </div>
              )}

              {/* "Amount to Finance" used to render HERE, above the tax and fees -- the same
                  number as "Total Financed" at the bottom, but positioned so it read like a
                  subtotal when it is in fact the final figure (it already includes the tax and
                  fees listed below it). Stated once, at the bottom, where it follows from the
                  lines above it (Jose 2026-07-12). */}

              {/* The tax line says WHY it is what it is -- a $0 tax, or a rate that isn't ours,
                  must never appear unexplained on a quote the customer sees. */}
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">
                  {calculation.taxTreatment === 'export'
                    ? calculation.taxExemptionReference?.trim()
                      ? 'Sales Tax — export exempt:'
                      : 'Sales Tax — export exempt (B/L pending):'
                    : calculation.taxTreatment === 'out_of_state'
                      ? `Sales Tax (${calculation.taxDestinationState || 'out-of-state'} ${calculation.taxRateApplied ?? 0}%):`
                      : 'Sales Tax:'}
                </span>
                <span className="font-semibold">
                  ${calculation.salesTax?.toLocaleString() || '0'}
                </span>
              </div>

              {/* Itemized fees — straight from the tenant's configured
                  deal_calculation_settings via the canonical preview endpoint
                  (WS3 Task 8). No longer summed from the separate
                  /settings/deal-summary fee editor, which could silently
                  disagree with the engine's own fee total. */}
              {(calculation.feesItemized || []).map((fee, idx) => (
                <div key={`${fee.name}-${idx}`} className="flex justify-between items-center py-1">
                  <span className="text-gray-600 text-sm">{fee.name}:</span>
                  <span className="font-medium text-sm">
                    ${parseFloat(fee.amount).toLocaleString()}
                  </span>
                </div>
              ))}

              {/* Out-the-door is what every buyer actually asks for: the full cost to drive
                  away, before anything is paid. It is NOT the tax base — a deposit/down
                  payment reduces the balance owed, never the sales tax. */}
              <div className="flex justify-between items-center py-1 border-t border-gray-200">
                <span className="text-gray-900 font-semibold text-sm">Total Out the Door:</span>
                <span className="font-bold text-base">
                  ${calculation.outTheDoor?.toLocaleString() || '0'}
                </span>
              </div>

              {isCashDeal ? (
                <>
                  {/* A cash deal has no loan, so Total Financed / Monthly Payment / Total of
                      Payments are meaningless here (they rendered as $0 before). What a cash
                      buyer needs is the deposit they've put down and what's still owed. */}
                  {(calculation.depositAmount || 0) > 0 && (
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-600 text-sm">Deposit (paid):</span>
                      <span className="font-medium text-sm text-green-600">
                        -${calculation.depositAmount?.toLocaleString() || '0'}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center py-1 bg-blue-50 px-2 rounded">
                    <span className="text-blue-900 font-semibold text-sm">Balance Due:</span>
                    <span className="font-bold text-lg text-blue-600">
                      ${calculation.balanceDue?.toLocaleString() || '0'}
                    </span>
                  </div>

                  {/* The export tax deposit is HELD, not charged -- it sits below the balance so
                      it never reads as tax on an exempt sale, and the buyer sees exactly what
                      they must hand over at delivery and what they get back. */}
                  {(calculation.exportTaxDeposit || 0) > 0 && (
                    <>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-600 text-sm">
                          Export Tax Deposit (refundable):
                        </span>
                        <span className="font-medium text-sm">
                          ${calculation.exportTaxDeposit?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-1 border-t">
                        <span className="text-gray-900 font-semibold text-sm">
                          Total Due at Delivery:
                        </span>
                        <span className="font-bold text-base">
                          ${calculation.dueAtDelivery?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <p className="text-xs text-amber-700 pt-1">
                        Refunded when the buyer presents the Bill of Lading. If the vehicle is never
                        exported, this covers the sales tax you will owe.
                      </p>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-gray-900 font-semibold text-sm">Total Financed:</span>
                    <span className="font-bold text-base">
                      ${calculation.totalAmountFinanced?.toLocaleString() || '0'}
                    </span>
                  </div>

                  {/* The payment alone doesn't say how many of them there are -- "$268.09
                      bi-weekly" reads very differently at 26 payments than at 52. Show the
                      count on the label (Jose 2026-07-12). */}
                  <div className="flex justify-between items-center py-1 bg-blue-50 px-2 rounded">
                    <span className="text-blue-900 font-semibold text-sm">
                      {paymentPeriodLabel} Payment
                      {calculation.paymentCount ? ` (${calculation.paymentCount})` : ''}:
                    </span>
                    <span className="font-bold text-lg text-blue-600">
                      ${calculation.monthlyPayment?.toLocaleString() || '0'}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-1">
                    <span className="text-gray-600 text-sm">Total Interest:</span>
                    <span className="font-medium text-sm">
                      ${calculation.financeCharge?.toLocaleString() || '0'}
                    </span>
                  </div>

                  {/* Renamed from "Total of Payments" (Jose 2026-07-12): it IS principal +
                      interest, exactly. NOTE the contract keeps the legally-defined TILA
                      wording "Total of Payments" -- see services/document_service.py's Fed Box.
                      Same number, different audience. */}
                  <div className="flex justify-between items-center py-1 border-t">
                    <span className="text-gray-900 font-semibold text-sm">
                      Principal + Interest Total:
                    </span>
                    <span className="font-semibold text-sm">
                      ${calculation.totalOfPayments?.toLocaleString() || '0'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={saveDealCalculation}
              disabled={saving || !calculation.vehiclePrice}
              className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save as Deal'}
            </button>

            {savedDealId && (
              <button
                onClick={openBillOfSaleDialog}
                className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white font-medium text-sm rounded-lg hover:bg-green-700"
              >
                <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                Generate Bill of Sale
              </button>
            )}

            <button
              onClick={printCalculation}
              className="w-full flex items-center justify-center px-4 py-2 bg-gray-600 text-white font-medium text-sm rounded-lg hover:bg-gray-700"
            >
              <PrinterIcon className="h-4 w-4 mr-2" />
              Print Calculator
            </button>

            {/* SMS/Email Functionality (if enabled and customer selected) */}
            {selectedCustomer &&
              (dealSettings.integrationOptions.enableSMS ||
                dealSettings.integrationOptions.enableEmail) && (
                <div className="border-t pt-3 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Send Deal Summary to Customer
                  </h4>
                  <div className="space-y-2">
                    {dealSettings.integrationOptions.enableSMS && (
                      <button
                        onClick={() => {
                          // SMS functionality would be implemented here
                          toast.success(
                            `SMS sent to ${selectedCustomer.first_name} ${selectedCustomer.last_name} at ${selectedCustomer.phone}`
                          );
                        }}
                        className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                      >
                        <PhoneIcon className="h-4 w-4 mr-2" />
                        Text to {selectedCustomer.phone}
                      </button>
                    )}

                    {dealSettings.integrationOptions.enableEmail && selectedCustomer.email && (
                      <button
                        onClick={() => {
                          // Email functionality would be implemented here
                          toast.success(
                            `Email sent to ${selectedCustomer.first_name} ${selectedCustomer.last_name} at ${selectedCustomer.email}`
                          );
                        }}
                        className="w-full flex items-center justify-center px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600"
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-2" />
                        Email to {selectedCustomer.email}
                      </button>
                    )}
                  </div>
                </div>
              )}

            {/* Integration Status Display */}
            {!selectedCustomer &&
              (dealSettings.integrationOptions.enableSMS ||
                dealSettings.integrationOptions.enableEmail) && (
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">
                    Select a customer to enable SMS/Email options
                  </p>
                </div>
              )}
          </div>
        </div>
      </AdaptiveGrid>

      {/* Trade-In Lookup Modal */}
      {showTradeInModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Trade-In Value Lookup</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  VIN (if available)
                </label>
                <input
                  type="text"
                  value={tradeInLookup.vin || ''}
                  onChange={(e) => setTradeInLookup((prev) => ({ ...prev, vin: e.target.value }))}
                  className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1HGBH41JXMN109186"
                />
              </div>

              <div className="text-center text-gray-500 text-sm">OR</div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <input
                    type="number"
                    value={tradeInLookup.year || ''}
                    onChange={(e) =>
                      setTradeInLookup((prev) => ({ ...prev, year: parseInt(e.target.value) }))
                    }
                    className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="2018"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                  <input
                    type="text"
                    value={tradeInLookup.make || ''}
                    onChange={(e) =>
                      setTradeInLookup((prev) => ({ ...prev, make: e.target.value }))
                    }
                    className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Honda"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                  <input
                    type="text"
                    value={tradeInLookup.model || ''}
                    onChange={(e) =>
                      setTradeInLookup((prev) => ({ ...prev, model: e.target.value }))
                    }
                    className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Civic"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mileage</label>
                  <input
                    type="number"
                    value={tradeInLookup.mileage || ''}
                    onChange={(e) =>
                      setTradeInLookup((prev) => ({ ...prev, mileage: parseInt(e.target.value) }))
                    }
                    className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="85000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                  <select
                    value={tradeInLookup.condition || 'good'}
                    onChange={(e) =>
                      setTradeInLookup((prev) => ({ ...prev, condition: e.target.value as any }))
                    }
                    className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                </div>
              </div>

              {tradeInLookup.estimatedValue && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <h4 className="font-semibold text-green-900">Estimated Trade Value</h4>
                  <p className="text-2xl font-bold text-green-600">
                    ${tradeInLookup.estimatedValue.toLocaleString()}
                  </p>
                  {tradeInLookup.kbbValue && (
                    <p className="text-sm text-green-700">
                      KBB: ${tradeInLookup.kbbValue.toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={lookupTradeInValue}
                disabled={calculating}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {calculating ? 'Looking Up...' : 'Look Up Value'}
              </button>
              <button
                onClick={() => setShowTradeInModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </FlexibleContainer>
  );
};

export default DealCalculatorPage;
